import type { TFunction } from 'i18next';
import {
  ACCOUNT_SETTINGS_TABS,
  CLERK_ORG_PROFILE_GENERAL_SEGMENT,
  ORGANIZATION_SETTINGS_GENERAL_HREF,
  ORGANIZATION_SETTINGS_TABS,
  isSettingsNavTabActive,
} from '@/lib/navigation/settings-nav-config';

function getSettingsSubRouteTitle(pathname: string, t: TFunction): string | null {
  const allTabs = [...ACCOUNT_SETTINGS_TABS, ...ORGANIZATION_SETTINGS_TABS];
  for (const tab of allTabs) {
    if (
      isSettingsNavTabActive(pathname, tab.href) &&
      tab.href !== '/settings' &&
      tab.href !== ORGANIZATION_SETTINGS_GENERAL_HREF
    ) {
      return t(tab.labelKey);
    }
  }
  return null;
}

/** Mobile header title for app routes (SaaS navbar + custom enterprise shell). */
export function getMobilePageTitle(pathname: string, t: TFunction): string | null {
  if (pathname === '/onboarding' || pathname === '/') {
    return null;
  }

  if (pathname === '/settings' || pathname.startsWith('/settings/general')) {
    return t('settings.general.title');
  }

  if (
    pathname === ORGANIZATION_SETTINGS_GENERAL_HREF ||
    pathname === `${ORGANIZATION_SETTINGS_GENERAL_HREF}/` ||
    pathname.startsWith(`${ORGANIZATION_SETTINGS_GENERAL_HREF}/general`) ||
    pathname.startsWith(`${ORGANIZATION_SETTINGS_GENERAL_HREF}/${CLERK_ORG_PROFILE_GENERAL_SEGMENT}`)
  ) {
    return t('settings.menu.organizationGeneral');
  }

  if (pathname.startsWith('/settings/')) {
    const subRouteTitle = getSettingsSubRouteTitle(pathname, t);
    if (subRouteTitle) {
      return subRouteTitle;
    }
    return t('account.settings');
  }

  const first = pathname.split('/')[1] || '';
  switch (first) {
    case 'dashboard':
      return t('navigation.dashboard');
    case 'chats':
      return t('navigation.chats');
    case 'customers':
      return t('navigation.customers');
    case 'channels': {
      if (pathname.startsWith('/channels/web')) {
        return t('navigation.webEmbed');
      }
      if (pathname.startsWith('/channels/whatsapp')) {
        return t('whatsapp.connectionPage.title');
      }
      return t('navigation.channel');
    }
    case 'agent':
      return t('navigation.settings');
    case 'advanced':
      return t('agent.tabs.advanced');
    case 'templates':
      return t('navigation.templates');
    case 'automations':
      return t('navigation.automations');
    case 'campaigns':
      return t('navigation.campaigns');
    case 'votes':
      return t('navigation.votes');
    case 'usage':
      return t('navigation.usage');
    case 'web-embed':
      return t('navigation.webEmbed');
    case 'whatsapp':
      if (pathname.startsWith('/whatsapp/channel/pricing')) {
        return t('whatsapp.pricingPage.title');
      }
      return t('whatsapp.connectionPage.title');
    case 'admin':
      return t('navigation.admin', 'Admin');
    case 'marketing':
      return t('navigation.marketing');
    case 'test-agent':
      return t('navigation.testChat');
    case 'billing':
      return t('navigation.billing');
    case 'partners':
      return t('navigation.partners');
    case 'achievements':
      return t('navigation.achievements');
    case 'reachstore':
      return t('navigation.store');
    case 'tickets':
      return t('tickets.title');
    default:
      return null;
  }
}
